import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { formatPhoneFromE164 } from '../lib/helpers';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Bot,
  MessageSquare,
  Users,
  Activity,
  RefreshCw,
  Settings,
  Send,
  Search,
  Clock,
  User,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Calendar,
  Eye,
  Download,
  Save,
  EyeOff,
  Upload,
  Link,
  FileText,
  Brain,
  Trash2,
  Plus,
  ExternalLink,
  BookOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppChatbotPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [chatSessions, setChatSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendMessageForm, setSendMessageForm] = useState({
    recipient_type: 'phone', // 'phone' ou 'employee'
    phone_number: '',
    employee_id: '',
    message: ''
  });
  const [employees, setEmployees] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Estados da integração Synkly (motor de WhatsApp da Maya)
  const [synklyStatus, setSynklyStatus] = useState({
    configured: false,
    connected: false,
    status: null,
    label: null,
    qr: null,
    last_error: null
  });
  const [synklyWebhookUrl, setSynklyWebhookUrl] = useState('');
  const [loadingSynkly, setLoadingSynkly] = useState(false);
  const [connectingSynkly, setConnectingSynkly] = useState(false);
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalMessages: 0,
    identifiedEmployees: 0,
    activeToday: 0
  });

  // Learning/Aprendizagem states
  const [documents, setDocuments] = useState([]);
  const [links, setLinks] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [newLink, setNewLink] = useState({ title: '', url: '', description: '' });
  const [addingLink, setAddingLink] = useState(false);
  
  // Maya Chat states
  const [mayaMessages, setMayaMessages] = useState([]);
  const [mayaMessage, setMayaMessage] = useState('');
  const [sendingToMaya, setSendingToMaya] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadConversations();
    loadChatSessions();
    loadEmployees();
    loadSynklyStatus();
  }, []);

  useEffect(() => {
    if (activeTab === 'conversations') {
      loadConversations();
    }
  }, [activeTab]);

  // Cleanup effect para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      setSelectedConversation(null);
      setConversations([]);
      setLoadingConversations(false);
    };
  }, []);

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Carregar estatísticas reais
      const statsResponse = await axios.get(`${API}/api/whatsapp/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Carregar funcionários para envio de mensagem
      const employeesResponse = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Usar os dados reais do backend
      setStats(statsResponse.data || {
        totalSessions: 0,
        totalMessages: 0,
        identifiedEmployees: 0,
        activeToday: 0
      });

      setEmployees(employeesResponse.data || []);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/api/whatsapp/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const conversationsData = response.data || [];
      setConversations(conversationsData);

      // Re-sincronizar a conversa aberta com o histórico atualizado
      setSelectedConversation(prev => {
        if (!prev) return prev;
        const updated = conversationsData.find(
          c => c.phone_number === prev.phone_number
        );
        return updated || prev;
      });
      
      // Atualizar estatísticas com dados reais
      const totalConversations = response.data?.length || 0;
      const messagesToday = response.data?.reduce((count, conv) => {
        const today = new Date().toISOString().split('T')[0];
        const todayMessages = conv.messages?.filter(msg => 
          msg.timestamp?.startsWith(today)
        ) || [];
        return count + todayMessages.length;
      }, 0) || 0;

      setStats(prev => ({
        ...prev,
        total_conversations: totalConversations,
        active_users: totalConversations,
        messages_today: messagesToday
      }));
      
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
      toast.error('Erro ao carregar conversas do WhatsApp');
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadSynklyStatus = async () => {
    try {
      setLoadingSynkly(true);
      const token = localStorage.getItem('token');
      const [statusResp, webhookResp] = await Promise.all([
        axios.get(`${API}/api/synkly/whatsapp/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/synkly/whatsapp/webhook-url`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} }))
      ]);

      const data = statusResp.data || {};
      setSynklyStatus({
        configured: !!data.configured,
        connected: data.status === 'connected',
        status: data.status || null,
        label: data.label || null,
        qr: data.qr || null,
        last_error: data.last_error || null
      });
      setSynklyWebhookUrl(webhookResp.data?.webhook_url || '');
    } catch (error) {
      console.error('Erro ao carregar status Synkly:', error);
      setSynklyStatus({
        configured: false,
        connected: false,
        status: null,
        label: null,
        qr: null,
        last_error: null
      });
    } finally {
      setLoadingSynkly(false);
    }
  };

  const loadChatSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/chat-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatSessions(response.data);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  const loadMessages = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/messages/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      toast.error('Erro ao carregar mensagens');
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    await loadMessages(session.id);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!sendMessageForm.message.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }

    if (sendMessageForm.recipient_type === 'phone' && !sendMessageForm.phone_number.trim()) {
      toast.error('Digite um número de telefone');
      return;
    }

    if (sendMessageForm.recipient_type === 'employee' && !sendMessageForm.employee_id) {
      toast.error('Selecione um funcionário');
      return;
    }

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('token');
      
      const messageData = {
        recipient_type: sendMessageForm.recipient_type,
        phone_number: sendMessageForm.phone_number,
        employee_id: sendMessageForm.employee_id,
        message: sendMessageForm.message
      };

      // Usar novo endpoint JSON para envio
      const messagePayload = {
        phone_number: sendMessageForm.phone_number,
        message: sendMessageForm.message
      };

      await axios.post(`${API}/api/whatsapp/send-message`, messagePayload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Mensagem enviada com sucesso!');
      
      // Limpar formulário
      setSendMessageForm({
        recipient_type: 'phone',
        phone_number: '',
        employee_id: '',
        message: ''
      });

      // Recarregar dados
      await loadDashboardData();

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleConnectSynkly = async () => {
    try {
      setConnectingSynkly(true);
      const token = localStorage.getItem('token');

      await axios.post(`${API}/api/synkly/whatsapp/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Conexão Synkly iniciada. Escaneie o QR Code se solicitado.');
      await loadSynklyStatus();
    } catch (error) {
      console.error('Erro ao conectar Synkly:', error);
      toast.error(error.response?.data?.detail || 'Erro ao conectar com a Synkly');
    } finally {
      setConnectingSynkly(false);
    }
  };

  const handleDisconnectSynkly = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp da Synkly?')) {
      return;
    }
    try {
      setConnectingSynkly(true);
      const token = localStorage.getItem('token');

      await axios.delete(`${API}/api/synkly/whatsapp/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('WhatsApp desconectado da Synkly.');
      await loadSynklyStatus();
    } catch (error) {
      console.error('Erro ao desconectar Synkly:', error);
      toast.error(error.response?.data?.detail || 'Erro ao desconectar da Synkly');
    } finally {
      setConnectingSynkly(false);
    }
  };

  const getSessionStatus = (session) => {
    const lastActivity = new Date(session.last_activity);
    const now = new Date();
    const diffHours = (now - lastActivity) / (1000 * 60 * 60);

    if (diffHours < 1) {
      return { label: 'Ativo', color: 'bg-green-500', icon: Activity };
    } else if (diffHours < 24) {
      return { label: 'Recente', color: 'bg-yellow-500', icon: Clock };
    } else {
      return { label: 'Inativo', color: 'bg-gray-500', icon: XCircle };
    }
  };

  const filteredSessions = chatSessions.filter(session => 
    session.phone_number.includes(searchTerm) ||
    (session.employee_name && session.employee_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Learning/Aprendizagem functions
  const loadDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const response = await axios.get(`${API}/api/maya/documents`);
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error('Erro ao carregar documentos');
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadLinks = async () => {
    try {
      const response = await axios.get(`${API}/api/maya/links`);
      setLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar links:', error);
      toast.error('Erro ao carregar links');
      setLinks([]);
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 10MB.');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/html'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não suportado. Use PDF, DOC, DOCX, TXT ou HTML.');
      return;
    }

    try {
      setUploadingDocument(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/api/maya/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Documento enviado com sucesso!');
      loadDocuments();
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Erro ao enviar documento:', error);
      toast.error('Erro ao enviar documento');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleAddLink = async () => {
    if (!newLink.title || !newLink.url) {
      toast.error('Título e URL são obrigatórios');
      return;
    }

    // Validate URL format
    try {
      new URL(newLink.url);
    } catch {
      toast.error('URL inválida');
      return;
    }

    try {
      setAddingLink(true);
      await axios.post(`${API}/api/maya/links`, newLink);
      
      toast.success('Link adicionado com sucesso!');
      setNewLink({ title: '', url: '', description: '' });
      loadLinks();
    } catch (error) {
      console.error('Erro ao adicionar link:', error);
      toast.error('Erro ao adicionar link');
    } finally {
      setAddingLink(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/maya/documents/${documentId}`);
      toast.success('Documento excluído com sucesso!');
      loadDocuments();
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      toast.error('Erro ao excluir documento');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Tem certeza que deseja excluir este link?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/maya/links/${linkId}`);
      toast.success('Link excluído com sucesso!');
      loadLinks();
    } catch (error) {
      console.error('Erro ao excluir link:', error);
      toast.error('Erro ao excluir link');
    }
  };

  const loadMayaChat = async () => {
    try {
      const response = await axios.get(`${API}/api/maya/chat/history`);
      setMayaMessages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar histórico do chat:', error);
      setMayaMessages([]);
    }
  };

  const sendMessageToMaya = async () => {
    if (!mayaMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      message: mayaMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
      is_from_user: true
    };

    setMayaMessages(prev => [...prev, userMessage]);
    const currentMessage = mayaMessage;
    setMayaMessage('');

    try {
      setSendingToMaya(true);
      const response = await axios.post(`${API}/api/maya/chat`, {
        message: currentMessage
      });

      const mayaResponse = {
        id: Date.now() + 1,
        message: response.data.response || 'Desculpe, não consegui processar sua mensagem.',
        sender: 'maya',
        timestamp: new Date().toISOString(),
        is_from_user: false
      };

      setMayaMessages(prev => [...prev, mayaResponse]);
    } catch (error) {
      console.error('Erro ao enviar mensagem para Maya:', error);
      toast.error('Erro ao enviar mensagem');
      
      const errorMessage = {
        id: Date.now() + 1,
        message: 'Desculpe, ocorreu um erro ao processar sua mensagem.',
        sender: 'maya',
        timestamp: new Date().toISOString(),
        is_from_user: false
      };

      setMayaMessages(prev => [...prev, errorMessage]);
    } finally {
      setSendingToMaya(false);
    }
  };

  const reprocessDocuments = async () => {
    try {
      setReprocessing(true);
      const response = await axios.post(`${API}/api/maya/documents/reprocess`);
      
      toast.success(`Reprocessamento concluído! ${response.data.processed} documento(s) processado(s)`);
      
      // Recarregar documentos para mostrar conteúdo atualizado
      loadDocuments();
    } catch (error) {
      console.error('Erro ao reprocessar documentos:', error);
      toast.error('Erro ao reprocessar documentos');
    } finally {
      setReprocessing(false);
    }
  };

  // Load learning data when tab is accessed
  useEffect(() => {
    if (activeTab === 'learning') {
      loadDocuments();
      loadLinks();
      loadMayaChat();
    }
  }, [activeTab]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Bot className="h-8 w-8 text-blue-600" />
            Maya - Assistente IA
          </h1>
          <p className="text-gray-600 mt-2">
            Gerencie conversas e monitore o chatbot WhatsApp
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={loadDashboardData}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Sessões</p>
                <p className="text-2xl font-bold text-blue-600">{stats.totalSessions}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Mensagens</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalMessages}</p>
              </div>
              <Send className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Funcionários ID</p>
                <p className="text-2xl font-bold text-purple-600">{stats.identifiedEmployees}</p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ativos Hoje</p>
                <p className="text-2xl font-bold text-orange-600">{stats.activeToday}</p>
              </div>
              <Activity className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 lg:grid-cols-5 h-12 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200/50">
          <TabsTrigger 
            value="dashboard" 
            className="text-xs lg:text-sm px-2 lg:px-4 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-green-500 data-[state=active]:text-white transition-all duration-200"
          >
            <span className="hidden sm:inline">Dashboard</span>
            <span className="sm:hidden">Home</span>
          </TabsTrigger>
          <TabsTrigger 
            value="conversations" 
            className="text-xs lg:text-sm px-2 lg:px-4 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-green-500 data-[state=active]:text-white transition-all duration-200"
          >
            <span className="hidden sm:inline">Conversas</span>
            <span className="sm:hidden">Chat</span>
          </TabsTrigger>
          <TabsTrigger 
            value="send-message" 
            className="text-xs lg:text-sm px-2 lg:px-4 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-green-500 data-[state=active]:text-white transition-all duration-200"
          >
            <span className="hidden sm:inline">Mensagem</span>
            <span className="sm:hidden">Enviar</span>
          </TabsTrigger>
          <TabsTrigger 
            value="learning" 
            className="text-xs lg:text-sm px-2 lg:px-4 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-green-500 data-[state=active]:text-white transition-all duration-200"
          >
            <span className="hidden sm:inline">Aprendizagem</span>
            <span className="sm:hidden">Docs</span>
          </TabsTrigger>
          <TabsTrigger 
            value="settings" 
            className="text-xs lg:text-sm px-2 lg:px-4 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-green-500 data-[state=active]:text-white transition-all duration-200"
          >
            <span className="hidden sm:inline">Configurações</span>
            <span className="sm:hidden">Config</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sessions Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Sessões Recentes
                </CardTitle>
                <CardDescription>
                  Últimas conversas com funcionários
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {chatSessions.slice(0, 5).map(session => {
                    const status = getSessionStatus(session);
                    const StatusIcon = status.icon;
                    
                    return (
                      <div key={session.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
                          <div>
                            <p className="font-medium">
                              {session.employee_name || 'Usuário não identificado'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatPhoneFromE164(session.phone_number)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-xs">
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(session.last_activity)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Estatísticas Rápidas
                </CardTitle>
                <CardDescription>
                  Métricas do chatbot
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Taxa de Identificação</span>
                    <span className="font-medium">
                      {stats.totalSessions > 0 ? Math.round((stats.identifiedEmployees / stats.totalSessions) * 100) : 0}%
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Mensagens por Sessão</span>
                    <span className="font-medium">
                      {stats.totalSessions > 0 ? Math.round(stats.totalMessages / stats.totalSessions) : 0}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Status do Sistema</span>
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Ativo
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Modelo IA</span>
                    <span className="font-medium text-sm">GPT-4o-mini</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Conversations Tab - Webhook Conversations */}
        <TabsContent value="conversations">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Conversas Maya IA</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadConversations}
                    disabled={loadingConversations}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingConversations ? 'animate-spin' : ''}`} />
                  </Button>
                </CardTitle>
                <CardDescription>
                  Conversas bidirecionais via webhook
                </CardDescription>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                {loadingConversations ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Carregando conversas...</p>
                  </div>
                ) : conversations.length > 0 ? (
                  <div className="space-y-2">
                    {conversations.map((conversation, index) => (
                      <div 
                        key={`conversation-${conversation.phone_number}-${conversation.last_timestamp || index}`}
                        onClick={() => setSelectedConversation(conversation)}
                        className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                          selectedConversation?.phone_number === conversation?.phone_number ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <p className="font-medium text-sm">
                                {conversation?.sender_name || 'Usuário'}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {conversation?.phone_number || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-600 mt-1 truncate">
                              {conversation?.last_message || 'Sem mensagens'}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="text-xs">
                              {conversation?.message_count || 0} msgs
                            </Badge>
                            <p className="text-xs text-gray-500 mt-1">
                              {conversation?.last_timestamp ? 
                                new Date(conversation.last_timestamp).toLocaleDateString() : ''
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-sm text-gray-500">
                      Nenhuma conversa encontrada
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      As conversas dos funcionários com a Maya aparecerão aqui
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chat Messages */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>
                  {selectedConversation ? (
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-blue-600" />
                      <div>
                        <span>{selectedConversation?.sender_name || 'Usuário'}</span>
                        <p className="text-sm font-normal text-gray-500">
                          {selectedConversation?.phone_number || 'N/A'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Mensagens da Conversa
                    </div>
                  )}
                </CardTitle>
                {selectedConversation && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{selectedConversation?.message_count || 0} mensagens</span>
                      <span>Última atividade: {
                        selectedConversation?.last_timestamp ? 
                          new Date(selectedConversation.last_timestamp).toLocaleString() : 'N/A'
                      }</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadConversations}
                      disabled={loadingConversations}
                      title="Atualizar histórico"
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingConversations ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                {selectedConversation && selectedConversation.messages?.length > 0 ? (
                  <div className="space-y-4">
                    {selectedConversation.messages.map((message, index) => (
                      <div 
                        key={`${message.id || message.timestamp || `msg-${index}`}`}
                        className={`flex ${
                          message.direction === 'outgoing' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.direction === 'outgoing'
                            ? 'bg-blue-100 text-blue-900 border border-blue-200' 
                            : 'bg-gray-100 text-gray-900 border border-gray-200'
                        }`}>
                          {message.direction === 'outgoing' && (
                            <div className="flex items-center gap-1 mb-1">
                              <Bot className="h-3 w-3 text-blue-600" />
                              <span className="text-xs font-medium text-blue-800">Maya IA</span>
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs opacity-70">
                              {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
                            </p>
                            {message.direction === 'outgoing' && (
                              <div className="flex items-center gap-1">
                                {message.status === 'sent' ? (
                                  <CheckCircle className="h-3 w-3 text-green-600" />
                                ) : message.status === 'failed' ? (
                                  <XCircle className="h-3 w-3 text-red-600" />
                                ) : (
                                  <Clock className="h-3 w-3 text-yellow-600" />
                                )}
                                <span className="text-xs">{message.status}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : selectedConversation ? (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Nenhuma mensagem nesta conversa</p>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Selecione uma conversa para visualizar as mensagens</p>
                    <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
                      Escolha um contato na lista ao lado para ver o histórico completo de mensagens trocadas com a Maya.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Send Message Tab */}
        <TabsContent value="send-message">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Send Message Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Enviar Mensagem via WhatsApp
                </CardTitle>
                <CardDescription>
                  Envie mensagens diretamente para funcionários via Maya
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  {/* Recipient Type */}
                  <div>
                    <Label htmlFor="recipient_type">Tipo de Destinatário</Label>
                    <select
                      id="recipient_type"
                      value={sendMessageForm.recipient_type}
                      onChange={(e) => setSendMessageForm(prev => ({ 
                        ...prev, 
                        recipient_type: e.target.value,
                        phone_number: '',
                        employee_id: ''
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="phone">Por Número de Telefone</option>
                      <option value="employee">Por Funcionário</option>
                    </select>
                  </div>

                  {/* Phone Number Input */}
                  {sendMessageForm.recipient_type === 'phone' && (
                    <div>
                      <Label htmlFor="phone_number">Número de Telefone</Label>
                      <Input
                        id="phone_number"
                        type="tel"
                        value={sendMessageForm.phone_number}
                        onChange={(e) => setSendMessageForm(prev => ({ ...prev, phone_number: e.target.value }))}
                        placeholder="Ex: +5511999999999 ou 11999999999"
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Digite o número com DDD (com ou sem +55)
                      </p>
                    </div>
                  )}

                  {/* Employee Selection */}
                  {sendMessageForm.recipient_type === 'employee' && (
                    <div>
                      <Label htmlFor="employee_id">Funcionário</Label>
                      <select
                        id="employee_id"
                        value={sendMessageForm.employee_id}
                        onChange={(e) => setSendMessageForm(prev => ({ ...prev, employee_id: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecione um funcionário</option>
                        {employees.map(employee => (
                          <option key={employee.id} value={employee.id}>
                            {employee.name} {employee.phone ? `- ${employee.phone}` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Apenas funcionários com telefone cadastrado
                      </p>
                    </div>
                  )}

                  {/* Message Content */}
                  <div>
                    <Label htmlFor="message">Mensagem</Label>
                    <textarea
                      id="message"
                      value={sendMessageForm.message}
                      onChange={(e) => setSendMessageForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Digite sua mensagem aqui..."
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      maxLength={1000}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-500">
                        Mensagens são enviadas em nome da Maya (assistente de RH)
                      </p>
                      <span className="text-xs text-gray-400">
                        {sendMessageForm.message.length}/1000
                      </span>
                    </div>
                  </div>

                  {/* Send Button */}
                  <Button 
                    type="submit" 
                    className="w-full flex items-center gap-2"
                    disabled={sendingMessage}
                  >
                    {sendingMessage ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Message Templates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Templates de Mensagem
                </CardTitle>
                <CardDescription>
                  Modelos prontos para usar
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Template Buttons */}
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2">Lembrete - Holerite</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      "Olá! Seu holerite já está disponível para consulta. Acesse o portal ou entre em contato com o RH."
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSendMessageForm(prev => ({ 
                        ...prev, 
                        message: "Olá! Seu holerite já está disponível para consulta. Acesse o portal ou entre em contato com o RH para mais informações." 
                      }))}
                    >
                      Usar Template
                    </Button>
                  </div>

                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2">Lembrete - Férias</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      "Lembrete: Você tem direito a férias. Entre em contato com o RH para agendar."
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSendMessageForm(prev => ({ 
                        ...prev, 
                        message: "Lembrete: Você tem direito a férias. Entre em contato com o RH para agendar suas férias e aproveitar seu período de descanso." 
                      }))}
                    >
                      Usar Template
                    </Button>
                  </div>

                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2">Comunicado Geral</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      "Comunicado importante da empresa. Verifique seu e-mail ou entre em contato com o RH."
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSendMessageForm(prev => ({ 
                        ...prev, 
                        message: "Temos um comunicado importante da empresa. Por favor, verifique seu e-mail corporativo ou entre em contato com o RH para mais informações." 
                      }))}
                    >
                      Usar Template
                    </Button>
                  </div>

                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2">Suporte Maya</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      "Olá! Sou Maya, sua assistente de RH. Como posso ajudá-lo hoje?"
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSendMessageForm(prev => ({ 
                        ...prev, 
                        message: "Olá! Sou Maya, sua assistente virtual de RH. Como posso ajudá-lo hoje? Posso responder dúvidas sobre holerites, férias, benefícios e muito mais! 😊" 
                      }))}
                    >
                      Usar Template
                    </Button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center text-sm text-gray-500">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    <span>As mensagens são enviadas em nome da Maya (assistente de RH)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Learning Tab */}
        <TabsContent value="learning">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Documents Section */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-blue-600" />
                    Upload de Documentos
                  </CardTitle>
                  <CardDescription>
                    Envie PDFs, documentos Word, texto ou HTML para treinar a Maya
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        id="document-upload"
                        accept=".pdf,.doc,.docx,.txt,.html"
                        onChange={handleDocumentUpload}
                        disabled={uploadingDocument}
                        className="hidden"
                      />
                      <label 
                        htmlFor="document-upload" 
                        className="cursor-pointer flex flex-col items-center"
                      >
                        {uploadingDocument ? (
                          <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mb-2" />
                        ) : (
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        )}
                        <span className="text-sm text-gray-600 mb-1">
                          {uploadingDocument ? 'Enviando...' : 'Clique para selecionar um arquivo'}
                        </span>
                        <span className="text-xs text-gray-400">
                          PDF, DOC, DOCX, TXT, HTML (máx. 10MB)
                        </span>
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link className="h-5 w-5 text-green-600" />
                    Adicionar Links
                  </CardTitle>
                  <CardDescription>
                    Adicione links de leis, regulamentos e documentos online
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="link-title">Título*</Label>
                      <Input
                        id="link-title"
                        value={newLink.title}
                        onChange={(e) => setNewLink(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Ex: CLT - Consolidação das Leis do Trabalho"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="link-url">URL*</Label>
                      <Input
                        id="link-url"
                        type="url"
                        value={newLink.url}
                        onChange={(e) => setNewLink(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://exemplo.com/documento"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="link-description">Descrição</Label>
                      <Input
                        id="link-description"
                        value={newLink.description}
                        onChange={(e) => setNewLink(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Descrição breve do conteúdo"
                      />
                    </div>

                    <Button 
                      onClick={handleAddLink}
                      disabled={addingLink || !newLink.title || !newLink.url}
                      className="w-full"
                    >
                      {addingLink ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Adicionar Link
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Knowledge Base Management */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    Documentos Carregados
                  </CardTitle>
                  <CardDescription>
                    {loadingDocuments ? 'Carregando...' : `${documents.length} documento(s) na base de conhecimento`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingDocuments ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Nenhum documento carregado</p>
                      <p className="text-sm">Envie documentos para treinar a Maya</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FileText className="h-4 w-4 text-blue-500" />
                            <div>
                              <p className="text-sm font-medium">{doc.filename || doc.title}</p>
                              <p className="text-xs text-gray-500">
                                {doc.size && `${Math.round(doc.size / 1024)} KB • `}
                                {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-5 w-5 text-green-600" />
                    Links Salvos
                  </CardTitle>
                  <CardDescription>
                    {links.length} link(s) para consulta
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {links.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Link className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Nenhum link salvo</p>
                      <p className="text-sm">Adicione links para referência da Maya</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {links.map((link) => (
                        <div key={link.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <ExternalLink className="h-4 w-4 text-green-500" />
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-blue-600 hover:underline"
                              >
                                {link.title}
                              </a>
                            </div>
                            {link.description && (
                              <p className="text-xs text-gray-500 mt-1">{link.description}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(link.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLink(link.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Actions Section */}
          <div className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-orange-600" />
                  Ações da Base de Conhecimento
                </CardTitle>
                <CardDescription>
                  Ferramentas para otimizar e processar documentos e links
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2">🔄 Reprocessar Documentos</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Extrai novamente o conteúdo de PDFs, DOCs e links para melhorar as respostas da Maya
                    </p>
                    <Button
                      onClick={reprocessDocuments}
                      disabled={reprocessing}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      {reprocessing ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Reprocessando...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Reprocessar Tudo
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2">📊 Status da Base</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Documentos:</span>
                        <span className="font-medium">{documents.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Links:</span>
                        <span className="font-medium">{links.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Processados:</span>
                        <span className="font-medium text-green-600">
                          {documents.filter(d => d.processed).length + links.filter(l => l.processed).length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Maya Chat Section */}
          <div className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-indigo-600" />
                  Chat com Maya IA
                </CardTitle>
                <CardDescription>
                  Converse diretamente com a Maya usando a base de conhecimento carregada
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Chat Messages */}
                <div className="border rounded-lg h-96 flex flex-col">
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {mayaMessages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        <div className="text-center">
                          <Bot className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>Inicie uma conversa com a Maya!</p>
                          <p className="text-sm">Faça perguntas sobre os documentos carregados</p>
                        </div>
                      </div>
                    ) : (
                      mayaMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.is_from_user ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              message.is_from_user
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <div className="flex items-center space-x-2 mb-1">
                              {message.is_from_user ? (
                                <User className="h-3 w-3" />
                              ) : (
                                <Bot className="h-3 w-3" />
                              )}
                              <span className="text-xs opacity-75">
                                {message.is_from_user ? 'Você' : 'Maya'}
                              </span>
                            </div>
                            <p className="text-sm">{message.message}</p>
                            <p className="text-xs opacity-75 mt-1">
                              {new Date(message.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="border-t p-4">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        sendMessageToMaya();
                      }}
                      className="flex space-x-2"
                    >
                      <Input
                        value={mayaMessage}
                        onChange={(e) => setMayaMessage(e.target.value)}
                        placeholder="Digite sua pergunta para a Maya..."
                        disabled={sendingToMaya}
                        className="flex-1"
                      />
                      <Button
                        type="submit"
                        disabled={sendingToMaya || !mayaMessage.trim()}
                      >
                        {sendingToMaya ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </form>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Synkly Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Integração Synkly WhatsApp
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadSynklyStatus}
                    disabled={loadingSynkly}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingSynkly ? 'animate-spin' : ''}`} />
                  </Button>
                </CardTitle>
                <CardDescription>
                  Conexão do WhatsApp da Maya via Synkly
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Synkly Status */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Status da Conexão</span>
                      {synklyStatus.connected ? (
                        <Badge variant="default" className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Conectado
                        </Badge>
                      ) : synklyStatus.configured ? (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {synklyStatus.status === 'pending' || synklyStatus.status === 'qr'
                            ? 'Aguardando QR Code'
                            : 'Não Conectado'}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          Não Configurado
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Plataforma</span>
                      <span className="text-sm text-gray-900">Synkly</span>
                    </div>

                    {synklyStatus.label && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Conta</span>
                        <span className="text-sm text-gray-900">{synklyStatus.label}</span>
                      </div>
                    )}

                    {synklyStatus.last_error && (
                      <div className="flex items-start justify-between mt-2">
                        <span className="text-sm text-gray-600">Último erro</span>
                        <span className="text-sm text-red-600 text-right ml-2">{synklyStatus.last_error}</span>
                      </div>
                    )}
                  </div>

                  {/* QR Code para conexão */}
                  {synklyStatus.qr && !synklyStatus.connected && (
                    <div className="p-4 bg-white border rounded-lg text-center">
                      <p className="text-sm text-gray-600 mb-3">
                        Escaneie o QR Code no WhatsApp do número da Maya para conectar:
                      </p>
                      <img
                        src={synklyStatus.qr.startsWith('data:') ? synklyStatus.qr : `data:image/png;base64,${synklyStatus.qr}`}
                        alt="QR Code Synkly"
                        className="mx-auto h-48 w-48 object-contain"
                      />
                    </div>
                  )}

                  {/* Webhook da Synkly */}
                  <div>
                    <Label>Webhook da Synkly</Label>
                    <code className="text-xs bg-gray-100 px-2 py-2 rounded mt-1 block font-mono break-all">
                      {synklyWebhookUrl || '/api/synkly/webhook'}
                    </code>
                    <p className="text-xs text-gray-500 mt-1">
                      Configure esta URL na sua conta Synkly para receber mensagens recebidas pela Maya.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    {synklyStatus.connected ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDisconnectSynkly}
                        disabled={connectingSynkly}
                        className="flex-1 text-orange-600 hover:text-orange-700"
                      >
                        {connectingSynkly ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <XCircle className="h-4 w-4 mr-2" />
                        )}
                        Desconectar
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleConnectSynkly}
                        disabled={connectingSynkly || !synklyStatus.configured}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        {connectingSynkly ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        {synklyStatus.qr ? 'Atualizar QR Code' : 'Conectar WhatsApp'}
                      </Button>
                    )}
                  </div>

                  {!synklyStatus.configured && (
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <h4 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Integração não configurada
                      </h4>
                      <p className="text-sm text-yellow-800">
                        Defina as variáveis <strong>SYNKLY_API_KEY</strong> e <strong>SYNKLY_BASE_URL</strong> no servidor para habilitar o WhatsApp da Maya via Synkly.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5" />
                  Configurações do Chatbot
                </CardTitle>
                <CardDescription>
                  Configure o comportamento da Maya
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="assistant_name">Nome do Assistente</Label>
                    <Input
                      id="assistant_name"
                      value="Maya"
                      disabled
                      className="bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Nome da assistente virtual de RH
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="model">Modelo de IA</Label>
                    <Input
                      id="model"
                      value="GPT-4o-mini + Fallback"
                      disabled
                      className="bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Modelo usado para gerar respostas
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Sistema de Fallback</Label>
                      <p className="text-xs text-gray-500">
                        Respostas automáticas quando IA falha
                      </p>
                    </div>
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Ativo
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Identificação Automática</Label>
                      <p className="text-xs text-gray-500">
                        Identifica funcionários por nome/CPF
                      </p>
                    </div>
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Ativo
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5" />
                  Status do Sistema
                </CardTitle>
                <CardDescription>
                  Informações técnicas do chatbot
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Integração Synkly</span>
                    {synklyStatus.configured ? (
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Configurada
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-red-100 text-red-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Não Configurada
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">WhatsApp Conectado</span>
                    {synklyStatus.connected ? (
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Sim
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Não Conectado
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Integração LLM</span>
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Fallback Ativo
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Base de Dados</span>
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Conectada
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Webhook da Synkly</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      /api/synkly/webhook
                    </code>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={loadSynklyStatus}
                    className="w-full"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Verificar Status
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg flex items-center space-x-3">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span>Carregando dados do chatbot...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppChatbotPage;